import type { Dispatch as ReduxDispatch, UnknownAction } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import Guid from "_shared/guids/guid";
import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import NotificationBatchSchema from "notifications/_schemas/notification-batch-schema";
import NotificationSchema from "notifications/_schemas/notification-schema";
import {
  addNotification,
  addNotifications,
  removeNotification,
} from "notifications/_stores/notifications-redux-slice";
import Session from "users/__session/session";
import Notification from "notifications/notification";
import MessageNotification from "notifications/message-notifications/message-notification";
import MessageNotificationSchema from "notifications/message-notifications/_schemas/message-notification-schema";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import { api } from "_shared/_stores/api";
import TopicCategory from "notifications/topics/topic-category";
import { sessionPermissionTag } from "permissions/_api/permissions-api-service";
import {
  myAccessRequestsTag,
  organizationAccessRequestsTag,
} from "permissions/access-requests/_api/access-requests-api";

const COMMUNICATIONS_INTERFACE_NAME = "communicationsPage";

const toNotification = (payload: unknown): Notification | MessageNotification | undefined => {
  // Try the more specific schema first
  if (MessageNotificationSchema.isValidJSON(payload)) {
    return MessageNotification.fromJSON(payload);
  }

  if (NotificationSchema.isValidJSON(payload)) {
    return Notification.fromJSON(payload);
  }

  return undefined;
};

const getVisibleSenderName = (notification: Notification | MessageNotification): string | null => {
  if ("messageInfo" in notification) {
    return notification.messageInfo?.senderName?.value ?? notification.senderName?.value ?? null;
  }

  return notification.senderName?.value ?? null;
};

const getVisibleSeenState = (notification: Notification | MessageNotification, userId?: Guid) => {
  if (!userId) {
    return {
      isSeen: false,
      seenAt: null,
    };
  }

  if (notification.senderUserId?.isEqualTo(userId)) {
    return {
      isSeen: true,
      seenAt: notification.createdDate?.toISOString?.() ?? null,
    };
  }

  const recipient = notification.recipients.find(({ userId: recipientUserId }) => recipientUserId.isEqualTo(userId));
  const seenAt = recipient?.seenAt?.toISOString?.() ?? null;

  return {
    isSeen: recipient?.isSeen ?? seenAt !== null,
    seenAt,
  };
};

const toVisibleNotificationProjection = (notification: Notification | MessageNotification, userId?: Guid) => {
  const senderUserId = notification.senderUserId?.value ?? null;
  const recipientIds = notification.recipients.map(({ userId: recipientUserId }) => recipientUserId.value);
  const isRecipient = !!userId && notification.recipients.some(({ userId: recipientUserId }) => recipientUserId.isEqualTo(userId));
  const seenState = getVisibleSeenState(notification, userId);

  return {
    id: notification.id.value,
    topicCategory: notification.topic?.category ?? null,
    actionRequired: notification.actionRequired ?? null,
    message: notification.message?.value ?? null,
    // An aggregation reuses the notification the recipient already holds (#830): same id, same
    // sender, same forum — only the count, the message text and the date move. Leaving the count out
    // of the projection would make a second message in a thread that repeats itself look like a
    // change to nothing at all, and the bell would never say "2 Messages from ...".
    occurrenceCount: notification.occurrenceCount ?? null,
    createdDate: notification.createdDate?.toISOString?.() ?? null,
    senderUserId,
    senderName: getVisibleSenderName(notification),
    recipientIds,
    recipientName: notification.recipientName?.value ?? null,
    userRole: userId ? (senderUserId === userId.value ? "sender" : isRecipient ? "recipient" : "other") : "unknown",
    isSeen: seenState.isSeen,
    seenAt: seenState.seenAt,
  };
};

const toNotificationIssuePlaceholder = (entry: unknown): Notification | MessageNotification | undefined => {
  if (!entry || typeof entry !== "object" || !("issue" in entry)) {
    return undefined;
  }

  const notificationId = getNotificationIdValue((entry as { id?: unknown }).id);
  if (!notificationId) {
    return undefined;
  }

  return {
    id: { value: notificationId },
  } as Notification | MessageNotification;
};

const getNotificationFromResponseEntry = (entry: unknown): Notification | MessageNotification | undefined => {
  if (!entry || typeof entry !== "object") {
    return undefined;
  }

  const notification = toNotification(entry);
  if (notification) {
    return notification;
  }

  return toNotificationIssuePlaceholder(entry);
};

const getNotificationIdValue = (value: unknown): string | undefined => {
  if (typeof value === "string") {
    return value;
  }

  if (value && typeof value === "object" && "value" in value) {
    const candidate = (value as { value?: unknown }).value;
    return typeof candidate === "string" ? candidate : undefined;
  }

  return undefined;
};

type NotificationsRealtimeState = {
  notifications: {
    byId: {
      entries: Record<string, unknown>;
    };
    interfaceQueries?: {
      lastSuccessfulResponses?: Record<string, { data?: unknown[] }>;
    };
  };
};

const getCurrentCommunicationsNotification = (
  state: {
    notifications: {
      interfaceQueries?: {
        lastSuccessfulResponses?: Record<string, { data?: unknown[] }>;
      };
    };
  },
  notificationId: string,
): Notification | MessageNotification | undefined => {
  const response = state.notifications.interfaceQueries?.lastSuccessfulResponses?.[COMMUNICATIONS_INTERFACE_NAME];
  const matchingEntry = response?.data?.find((entry) => {
    if (!entry || typeof entry !== "object" || !("id" in entry)) {
      return false;
    }

    return getNotificationIdValue((entry as { id?: unknown }).id) === notificationId;
  });

  return getNotificationFromResponseEntry(matchingEntry);
};

/**
 * Whether an update to a notification this browser has never loaded is one the reader should now be
 * shown: it is addressed to them and they have not read it.
 *
 * A message folded into an unread notification (#830) arrives as `notification-updated`, because the
 * row already exists on the server. If that row was not loaded here — past the first page, or the
 * page was opened before it was written — the fold was dropped, and the row never appeared, moved to
 * the top, or counted as unread until a reload (#5825). An unread row addressed to the reader is news
 * however it reached them, so it is taken the way `notification-received` would be.
 *
 * Anything else about an unloaded notification still is not: a read row, or one the reader only
 * sent, would put old rows into the bell that nobody asked to see. Archiving is not judged here; the
 * payload does not carry it, and storing the row clears the query cache, so every list re-reads
 * from the API, which leaves archived rows out.
 */
const isUnreadNewsForReader = (notification: Notification | MessageNotification, currentUserId?: Guid): boolean => {
  if (!currentUserId) return false;

  const recipient = notification.recipients.find(({ userId }) => userId.isEqualTo(currentUserId));
  return !!recipient && !recipient.isSeen;
};

/**
 * Whether an update the server announced changes anything this browser is showing. An update whose
 * visible fields all match what it already holds is a render for no reason. An update to a
 * notification the browser has never loaded matters only when it is unread news for the reader.
 */
const isVisibleUpdate = (
  state: NotificationsRealtimeState,
  notification: Notification | MessageNotification,
  currentUserId?: Guid,
): boolean => {
  const currentCommunicationsNotification = getCurrentCommunicationsNotification(state, notification.id.value);
  const existingNotificationJson = state.notifications.byId.entries[notification.id.value];
  const existingNotification = existingNotificationJson
    ? toNotification(existingNotificationJson)
    : currentCommunicationsNotification;

  if (!currentCommunicationsNotification && !existingNotification) {
    return isUnreadNewsForReader(notification, currentUserId);
  }

  if (currentCommunicationsNotification) {
    return true;
  }

  return !(
    existingNotification &&
    isEqual(
      toVisibleNotificationProjection(existingNotification, currentUserId),
      toVisibleNotificationProjection(notification, currentUserId),
    )
  );
};

type Dispatch = ReduxDispatch<UnknownAction>;

/**
 * An access request notification addressed to the reader means their access or their requests have
 * moved (#6596). A grantor is asked about a new request, so the organization's requests are read
 * again. A requester is told theirs was decided, and an approval granted them something, so their
 * session's grants are read again too: the control that offered the request enables without a reload.
 */
export function invalidateAccessRequestState(
  dispatch: Dispatch,
  notification: Notification | MessageNotification,
  readCurrentUserId: () => Guid | undefined,
): void {
  if (notification.topic?.category !== TopicCategory.Permissions.AccessRequest) return;
  const currentUserId = readCurrentUserId();
  if (!currentUserId ||!notification.recipients.some(({ userId }) => userId.isEqualTo(currentUserId))) return;

  dispatch(api.util.invalidateTags([sessionPermissionTag, myAccessRequestsTag, organizationAccessRequestsTag]));
}

/**
 * A grantor's prompt changes when another grantor decides the request: its action is cleared. The
 * organization's requests are read again, so the row moves to the history on every grantor's page.
 */
function invalidateDecidedAccessRequest(dispatch: Dispatch, notification: Notification | MessageNotification): void {
  if (notification.topic?.category !== TopicCategory.Permissions.AccessRequest) return;
  dispatch(api.util.invalidateTags([organizationAccessRequestsTag]));
}

const callbacks = withCallbacks()
  .add("notification-received", (payload: unknown) => (dispatch) => {
    const notification = toNotification(payload);
    if (notification) {
      dispatch(addNotification(notification));
      invalidateAccessRequestState(dispatch, notification, () => Session.loadFromStorage(() => {})?.user?.id);
    }
  })
  .add("notification-updated", (payload: unknown) => (dispatch, getState) => {
    const notification = toNotification(payload);
    if (!notification) return;
    invalidateDecidedAccessRequest(dispatch, notification);

    const currentUserId = Session.loadFromStorage(() => {}).user?.id;
    const state = getState() as NotificationsRealtimeState;
    if (!isVisibleUpdate(state, notification, currentUserId)) return;

    dispatch(addNotification(notification));
  })
  /**
   * The whole of one bulk change, in one frame and one store update (GitHub issue #2963).
   *
   * Marking a screenful of notifications seen used to arrive as one `notification-updated` per row,
   * so the browser re-rendered once per row. Every notification in the batch is now folded into a
   * single `addNotifications` dispatch, which writes them all and clears the query cache once.
   *
   * Entries are parsed one at a time, so an entry this bundle cannot read costs that entry rather
   * than the batch. The RTK Query by-id caches are deliberately left alone, exactly as the
   * single-notification event leaves them: the acting browser's own mutation already upserts what it
   * received, and every other browser reads through the slice.
   */
  .add("notifications-bulk-updated", (payload: unknown) => (dispatch, getState) => {
    if (!NotificationBatchSchema.isValidJSON(payload)) return;

    const currentUserId = Session.loadFromStorage(() => {}).user?.id;
    const state = getState() as NotificationsRealtimeState;
    const updatedNotifications = payload.notifications
      .map((entry) => toNotification(entry))
      .filter(
        (notification): notification is Notification | MessageNotification =>
          !!notification && isVisibleUpdate(state, notification, currentUserId),
      );

    if (updatedNotifications.length === 0) return;

    dispatch(addNotifications(updatedNotifications));
  })
  .add("notification-deleted", (payload: unknown) => (dispatch) => {
    const deletedNotificationId = new Guid((payload as { id: string }).id);
    dispatch(removeNotification(deletedNotificationId));
  });

export const notificationsAblyReduxMiddleware = reduxAblyMiddleware({
  callbacks,
  channelName: ablyChannelNamesForSession.notifications,
  connectionHealth: { key: "notifications", label: "Notifications" },
  shouldConnectionStartImmediately: true,
});

